import * as React from "react";
import { Outlet } from "react-router-dom";
import { SidebarLayout, SidebarProvider } from "@/components/layout/sidebar/Sidebar";
import * as Icons from "@/assets/features/sidebar";
import Navbar from "@/components/layout/Navbar";
import { useIsIncludeCompany, useUser } from "@/stores/authStore";
import { useEffectiveDocumentDir } from "@/hooks/useEffectiveDocumentDir";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function MainLayout() {
  const { t, i18n } = useTranslation();
  const shellDir = useEffectiveDocumentDir();
  const baseLang = String(i18n.resolvedLanguage ?? i18n.language ?? "en")
    .split(/[-_/]/)[0]
    .toLowerCase();
  const useRtlMirroredShell = shellDir === "rtl" || baseLang === "ar";
  const isIncludeCompany = useIsIncludeCompany();
  const user = useUser();

  const sidebarItems = [
    {
      label: t("sidebar.sections.menu"),
      children: [
        {
          label: t("sidebar.dashboard"),
          path: "/dashboard",
          icon: Icons.Dashboard,
        },
        ...(user?.isAgency
          ? [
              {
                label: t("sidebar.clientsBoard"),
                path: "/clients-board",
                icon: Icons.ClientBoard,
              },
              {
                label: t("sidebar.clientsReports"),
                path: "/clients-reports",
                icon: Icons.ClientReports,
              },
            ]
          : []),
      ],
    },
    {
      label: t("sidebar.sections.recruitment"),
      children: [
        {
          label: t("sidebar.jobRequisition"),
          path: "/job-requisition",
          icon: Icons.Requisition,
          subitems: [
            {
              label: t("sidebar.jobRequisitionNew"),
              path: "/job-requisition/new",
            },
            {
              label: t("sidebar.jobRequisitionManagement"),
              path: "/job-requisition",
            },
          ],
        },
        {
          label: t("sidebar.jobRequests"),
          path: "/job-requests",
          icon: Icons.JobRequets,
        },
        {
          label: t("sidebar.evaluationForms"),
          path: "/evaluation-forms",
          icon: Icons.EvaluationForms,
        },
        {
          label: t("sidebar.positions"),
          path: "/positions",
          icon: Icons.Positions,
        },
        {
          label: t("sidebar.candidates"),
          path: "/candidates-managment",
          icon: Icons.Candidates,
        },
      ],
    },
    {
      label: t("sidebar.sections.organization"),
      children: [
        {
          label: t("sidebar.reports"),
          path: "/reports",
          icon: Icons.Reports,
          subitems: [
            {
              label: t("sidebar.reportsPerformanceAnalytics"),
              path: "/reports/performance-analytics",
            },
            {
              label: t("sidebar.reportsApplicationCharts"),
              path: "/reports/application-charts",
            },
            {
              label: t("sidebar.reportsPositionCharts"),
              path: "/reports/position-charts",
            },
            {
              label: t("sidebar.reportsSourcingCharts"),
              path: "/reports/sourcing-charts",
            },
            {
              label: t("sidebar.reportsInterviewAnalytics"),
              path: "/reports/interview-analytics",
            },
            {
              label: t("sidebar.reportsRequisitionCharts"),
              path: "/reports/requisition-charts",
            },
            {
              label: t("sidebar.reportsCandidatePipelineFlow"),
              path: "/reports/candidate-pipeline-flow",
            },
            {
              label: t("sidebar.reportsEvaluation"),
              path: "/reports/evaluation-report",
            },
            {
              label: t("sidebar.reportsCandidateCharts"),
              path: "/reports/candidate-charts",
            },
            {
              label: t("sidebar.reportsBlacklistedCandidate"),
              path: "/reports/blacklisted-candidate-report",
            },
          ],
        },
        {
          label: t("sidebar.settings"),
          path: "/settings",
          icon: Icons.Settings,
          subitems: [
            {
              label: t("sidebar.settingsDefaults"),
              path: "/settings/defaults",
            },
            {
              label: t("sidebar.settingsConfiguration"),
              path: "/settings/configuration",
            }
          ],
        },
        {
          label: t("sidebar.dataManagement"),
          path: "/data-managment",
          icon: Icons.DataManagment,
        },
      ],
    },
  ];

  const hasSidebarChrome = isIncludeCompany;
  const sidebar = hasSidebarChrome ? (
    <SidebarLayout
      className="sticky top-0 max-h-screen min-h-0 h-full"
      items={sidebarItems}
    />
  ) : null;

  const main = (
    <main
      dir={useRtlMirroredShell ? "rtl" : "ltr"}
      lang={i18n.resolvedLanguage ?? i18n.language}
      className={cn(
        "min-h-0 min-w-0 scroll-y-overlay overflow-x-hidden bg-primary-foreground px-[2.62rem] pb-12 pt-[1rem]",
        useRtlMirroredShell && "rtl",
      )}
    >
      <Navbar />
      <Outlet />
    </main>
  );

  return (
    <SidebarProvider>
      <div
        dir="ltr"
        className={cn(
          "grid h-screen min-h-0 w-full grid-rows-1 items-stretch overflow-hidden text-foreground",
          !hasSidebarChrome && "grid-cols-1",
          hasSidebarChrome &&
            (useRtlMirroredShell
              ? "grid-cols-[minmax(0,1fr)_auto]"
              : "grid-cols-[auto_minmax(0,1fr)]"),
        )}
      >
        {hasSidebarChrome ? (
          useRtlMirroredShell ? (
            <>
              {main}
              {sidebar}
            </>
          ) : (
            <>
              {sidebar}
              {main}
            </>
          )
        ) : (
          main
        )}
      </div>
    </SidebarProvider>
  );
}
